import { useState, useEffect } from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  ClipboardList, Compass, ListChecks, ShieldAlert, Target, ExternalLink,
  MessageSquarePlus, Trash2, CheckCircle2, Loader2, Info, Gauge,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
  PRIORITIES, STATUSES, PRIORITY_STYLES, STATUS_STYLES, fmtDate,
} from './plannerConstants';

const GuideBlock = ({ icon: Icon, title, tint, children, testid }) => (
  <section className="rounded-lg border border-border bg-muted/20 overflow-hidden" data-testid={testid}>
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-muted/40">
      <Icon className={`h-4 w-4 ${tint}`} />
      <h4 className="text-xs font-semibold uppercase tracking-wide text-foreground/80">{title}</h4>
    </div>
    <div className="px-4 py-3 text-sm text-muted-foreground leading-relaxed">{children}</div>
  </section>
);

export function TaskDetailDrawer({ open, onOpenChange, task, accountId, members, navigate, onChanged, planCompleted }) {
  const [local, setLocal] = useState(task);
  const [updates, setUpdates] = useState([]);
  const [noteBody, setNoteBody] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setLocal(task); }, [task]);

  useEffect(() => {
    if (!open || !task?.id) return;
    api.get(`/onboarding-planner/tasks/${task.id}/updates`, { params: { account_id: accountId } })
      .then(({ data }) => setUpdates(data.updates || []))
      .catch(() => setUpdates([]));
  }, [open, task?.id, accountId]);

  if (!local) return null;

  const patch = async (fields) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/onboarding-planner/tasks/${local.id}`,
        fields, { params: { account_id: accountId } });
      setLocal(data.task);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const addNote = async () => {
    if (!noteBody.trim()) return;
    setSavingNote(true);
    try {
      const { data } = await api.post(`/onboarding-planner/tasks/${local.id}/updates`,
        { body: noteBody }, { params: { account_id: accountId } });
      setUpdates((u) => [...u, data.update]);
      setNoteBody('');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add note');
    } finally {
      setSavingNote(false);
    }
  };

  const deleteTask = async () => {
    if (!window.confirm('Delete this custom task?')) return;
    try {
      await api.delete(`/onboarding-planner/tasks/${local.id}`, { params: { account_id: accountId } });
      toast.success('Task deleted');
      onOpenChange(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const nav = local.navigation_guidance || {};
  const vi = local.volume_info || {};
  const statusOptions = STATUSES.filter((s) => s !== 'Not Applicable' || local.allow_not_applicable);
  const readOnly = planCompleted;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="task-detail-drawer">
        <div className="sticky top-0 z-10 bg-background border-b border-border px-6 py-4">
          <SheetHeader>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="text-[10px]">{local.is_custom ? 'Custom' : 'Standard'}</Badge>
              <Badge variant="outline" className="text-[10px]">{local.phase}</Badge>
              {local.mandatory && <Badge className="text-[10px] bg-indigo-100 text-indigo-800 border-indigo-200">Mandatory</Badge>}
              {local.overdue && <Badge className="text-[10px] bg-rose-100 text-rose-800 border-rose-200">Overdue</Badge>}
            </div>
            <SheetTitle className="flex items-center gap-2 text-lg">{local.title}</SheetTitle>
            <SheetDescription>{local.task_detail}</SheetDescription>
          </SheetHeader>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Operational controls */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Status</Label>
              <Select value={local.status} onValueChange={(v) => patch({ status: v })} disabled={readOnly}>
                <SelectTrigger data-testid="task-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {statusOptions.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`task-status-${s.replace(/\s/g, '-').toLowerCase()}`}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Priority</Label>
              <Select value={local.priority} onValueChange={(v) => patch({ priority: v })} disabled={readOnly}>
                <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs">Assigned To</Label>
              <Select value={local.assigned_user_id || 'unassigned'}
                onValueChange={(v) => patch({ assigned_user_id: v === 'unassigned' ? null : v })} disabled={readOnly}>
                <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} <span className="text-muted-foreground">· {m.role}</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Planned Start</Label>
              <Input type="date" value={(local.planned_start_date || '').slice(0, 10)}
                onChange={(e) => patch({ planned_start_date: e.target.value })} disabled={readOnly} data-testid="task-start-date" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Planned End</Label>
              <Input type="date" value={(local.planned_end_date || '').slice(0, 10)}
                onChange={(e) => patch({ planned_end_date: e.target.value })} disabled={readOnly} data-testid="task-end-date" />
            </div>
          </div>

          {local.status === 'Blocked' && (
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1 text-rose-700"><ShieldAlert className="h-3.5 w-3.5" /> Blocked Reason (required)</Label>
              <Textarea value={local.blocked_reason || ''} placeholder="What is blocking this task?"
                onBlur={(e) => e.target.value.trim() && patch({ status: 'Blocked', blocked_reason: e.target.value })}
                onChange={(e) => setLocal({ ...local, blocked_reason: e.target.value })}
                disabled={readOnly} data-testid="task-blocked-reason" />
            </div>
          )}

          {local.actual_completion_date && (
            <div className="flex items-center gap-2 text-sm text-emerald-700">
              <CheckCircle2 className="h-4 w-4" /> Completed on {fmtDate(local.actual_completion_date)}
            </div>
          )}

          {/* Volume */}
          {vi.enabled && (
            <GuideBlock icon={Gauge} title="Portfolio Volume" tint="text-cyan-600" testid="task-volume-block">
              <div className="flex items-center gap-6 flex-wrap">
                <div>
                  <div className="text-2xl font-bold text-foreground">{vi.display || '—'}</div>
                  <div className="text-xs">{vi.unit} {vi.resolved_by === 'auto' && <span className="text-emerald-600">(live from BillPortal)</span>}</div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Expected total (target)</Label>
                  <Input type="number" min="0" className="w-32" value={local.expected_volume ?? ''}
                    onChange={(e) => setLocal({ ...local, expected_volume: e.target.value })}
                    onBlur={(e) => patch({ expected_volume: e.target.value })}
                    disabled={readOnly} data-testid="task-expected-volume" />
                </div>
                {vi.resolved_by === 'manual' && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">Current count (manual)</Label>
                    <Input type="number" min="0" className="w-32" value={local.current_volume_manual ?? ''}
                      onChange={(e) => setLocal({ ...local, current_volume_manual: e.target.value })}
                      onBlur={(e) => patch({ current_volume_manual: e.target.value })}
                      disabled={readOnly} data-testid="task-current-volume" />
                  </div>
                )}
              </div>
            </GuideBlock>
          )}

          {/* Guidance */}
          {!local.is_custom && (
            <>
              {(local.prerequisites_guidance || []).length > 0 && (
                <GuideBlock icon={Info} title="Prerequisites / Dependencies" tint="text-amber-600" testid="task-prereqs">
                  <ul className="list-disc pl-5 space-y-1.5">
                    {local.prerequisites_guidance.map((p, i) => <li key={i}>{p}</li>)}
                  </ul>
                </GuideBlock>
              )}

              {nav.label && (
                <GuideBlock icon={Compass} title="Where to Navigate" tint="text-blue-600" testid="task-navigation">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-medium text-foreground">{nav.label}</span>
                    {nav.deep_link && (
                      <Button size="sm" variant="outline" data-testid="task-goto-setup"
                        onClick={() => navigate(nav.deep_link)}>
                        <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Go to Setup
                      </Button>
                    )}
                  </div>
                </GuideBlock>
              )}

              {(local.setup_steps_guidance || []).length > 0 && (
                <GuideBlock icon={ListChecks} title="How to Set Up" tint="text-emerald-600" testid="task-setup-steps">
                  <ol className="space-y-2.5">
                    {local.setup_steps_guidance.map((s, i) => (
                      <li key={i} className="flex gap-3">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-[11px] font-bold">{i + 1}</span>
                        <span className="text-foreground/80">{s}</span>
                      </li>
                    ))}
                  </ol>
                </GuideBlock>
              )}

              {local.completion_check && (
                <GuideBlock icon={Target} title="Expected Outcome" tint="text-fuchsia-600" testid="task-completion-check">
                  {local.completion_check}
                </GuideBlock>
              )}
            </>
          )}

          {/* Updates */}
          <GuideBlock icon={ClipboardList} title="Notes / Updates" tint="text-sky-600" testid="task-updates">
            <div className="space-y-3">
              {updates.length === 0 && <p className="text-xs italic">No updates yet.</p>}
              {updates.map((u) => (
                <div key={u.id} className="rounded-md border border-border bg-card px-3 py-2" data-testid="task-update-item">
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                    <span className="font-medium text-foreground/70">{u.author_name}</span>
                    <span>{fmtDate(u.created_at)}</span>
                  </div>
                  <p className="text-sm text-foreground/80 mt-1">{u.body}</p>
                </div>
              ))}
              {!readOnly && (
                <div className="flex gap-2 pt-1">
                  <Textarea rows={2} placeholder="Add an update…" value={noteBody}
                    onChange={(e) => setNoteBody(e.target.value)} data-testid="task-note-input" />
                  <Button size="sm" onClick={addNote} disabled={savingNote || !noteBody.trim()} data-testid="task-note-add">
                    {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquarePlus className="h-4 w-4" />}
                  </Button>
                </div>
              )}
            </div>
          </GuideBlock>

          {local.is_custom && !readOnly && (
            <Button variant="destructive" size="sm" onClick={deleteTask} data-testid="task-delete-custom">
              <Trash2 className="h-4 w-4 mr-1.5" /> Delete custom task
            </Button>
          )}
          {busy && <div className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Saving…</div>}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default TaskDetailDrawer;
